import { Button, TableRow } from '@sam/components';
import { AgendaEntry, AgendaEntryGroup } from '@sam/components/src/Agenda/Agenda.types';
import dayjs, { Dayjs } from 'dayjs';
import { NavigateFunction } from 'react-router-dom';
import { AbsenceReason, Holiday, Language, SimpleUser } from 'shared';
import { Right, UserRight } from 'shared/src/userRole/UserRole.types';
import i18n from '../../i18n/i18n';
import { getUserNameForSimpleUser, isUserAllowedToDo } from '../user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

/**
 * Helper to transform given holidays to Tablerows
 * @param holidays to create rows from
 * @param navigate to create Icons
 * @returns Tablerows
 */
export const convertHolidaysIntoTableEntries = (
  userRights: UserRight,
  holidays: Holiday[],
  users: SimpleUser[],
  navigate: NavigateFunction,
): TableRow[] => {
  return holidays.map((holiday) => ({
    id: holiday.id,
    content: [
      getUserNameForSimpleUser(holiday.userId, users),
      holiday.startDate.toLocaleDateString('de-DE'),
      holiday.endDate.toLocaleDateString('de-DE'),
      holiday.totalAmount,
      <div>
        {isUserAllowedToDo(userRights, Right.HOLIDAY_READ) && (
          <IconComponent
            type={IconType.PREVIEW}
            width={40}
            onClick={() => navigate(`/holiday/create`, { state: { holiday } })}
          />
        )}
      </div>,
    ],
  }));
};

export const generateEmptyHoliday = (override?: Partial<Holiday>): Holiday => ({
  approved: false,
  createDate: new Date(),
  denied: false,
  deputyId: '',
  endDate: new Date(),
  changes: [],
  id: undefined!,
  officeId: '',
  startDate: new Date(),
  totalAmount: 1,
  userId: '',
  ...override,
});

/**
 * Util method to check if a exsisting holidayrequest has an overlap with a new range
 * @param newHoliday the new holiday instance
 * @param existingHoliday existing requests to check for an overlap
 * @returns true if an overlap exists
 * @tested
 */
export const holidayHasOverlap = (newHoliday: Holiday, existingHoliday: Holiday[]): boolean => {
  return existingHoliday.some((holiday) => {
    return (
      !holiday.denied &&
      dayjs(newHoliday.startDate).isBefore(holiday.endDate) &&
      dayjs(newHoliday.endDate).isAfter(holiday.startDate)
    );
  });
};

/**
 *  Helper method to calculate vacation days left
 * @param totalVacationDays of the user
 * @param existingHolidays to subtract from the total days
 * @returns number of vacation days left
 */
export const calculateVacationDaysLeft = (
  totalVacationDays: number,
  existingHolidays: Holiday[],
): number => {
  let vacationDaysUsed: number = 0;
  existingHolidays.map((holiday) => (vacationDaysUsed += holiday.totalAmount));
  return totalVacationDays - vacationDaysUsed;
};

/**
 * Util method to check if a day is in a given range
 * @param day to check
 * @param startDate of the range to check
 * @param endDate of the range to check
 * @returns boolean if the date is in the range or not
 */
export const checkIfDayIsInRange = (day: Dayjs, startDate: Date, endDate: Date): boolean =>
  day.isSame(startDate, 'date') ||
  (day.isAfter(startDate) && day.isBefore(endDate)) ||
  day.isSame(endDate);

/**
 * Util method to convert holidayRequests to TableEntries
 * @param holidayRequests to convert into TableEntries
 * @param users to get the name of the requesting persons
 * @returns Array of TableRows
 */
export const convertHolidayRequestsToTableEntries = (
  holidayRequests: Holiday[],
  users: SimpleUser[],
  navigate: NavigateFunction,
  absenceReasons: AbsenceReason[],
  language?: Language,
): TableRow[] => {
  return holidayRequests.map((request) => {
    const reason: AbsenceReason | undefined = absenceReasons.find(
      (reason) => reason.id === request.reasonId,
    );
    const name: string = getTranslatedName(reason, language);
    return {
      id: request.id,
      content: [
        getUserNameForSimpleUser(request.userId, users),
        name,
        request.createDate.toLocaleDateString('de-DE'),
        request.startDate.toLocaleDateString('de-DE'),
        request.endDate.toLocaleDateString('de-DE'),
        request.totalAmount,
        <Button
          value={i18n.t('general.buttons.edit')}
          maxWidth={100}
          disabled={request.approved || request.denied}
          onClick={() => navigate(`/holiday/edit/decide`, { state: { request } })}
        />,
        <div className="holiday-requests__table-icon-wrapper">
          {request.approved ? (
            <IconComponent type={IconType.APPROVED} width={30} />
          ) : request.denied ? (
            <IconComponent type={IconType.DENIED} width={30} />
          ) : (
            <IconComponent type={IconType.PENDING} width={30} />
          )}
        </div>,
      ],
    };
  });
};

/**
 * Util method that combines absences and holidays to AgendaEntries
 * @param users list of all users that are present in the absences and holidays to get the name from
 * @param absences absences that should be converted and displayed in the Agenda
 * @param holidays holidays that should be converted and displayed in the Agenda
 * @returns Array of AgendaEntries
 */
export const generateAgendaEntries = (
  users: SimpleUser[],
  holidays: Holiday[],
): AgendaEntryGroup[] => {
  const groups: AgendaEntryGroup[] = users.map((user) => {
    const entries: AgendaEntry[] = holidays
      .filter((holiday) => holiday.userId === user.id && !holiday.denied)
      .map((holiday) => ({
        id: holiday.id,
        background: holiday.denied || holiday.approved ? '#34B15E' : '#D3F4E4',
        border: `1px solid #34B15E`,
        start: holiday.startDate.toISOString().split('T')[0],
        end: holiday.endDate.toISOString().split('T')[0],
        title: i18n.t('general.agendaEntries.holiday'),
        onClick: () => {},
        color: holiday.denied || holiday.approved ? 'white' : 'black',
      }));

    const group: AgendaEntryGroup = {
      groupName: getUserNameForSimpleUser(user.id, users),
      id: user.id,
      entries,
    };

    return group;
  });

  return groups;
};

/**
 * Util method to get the translated name of a reason
 * @param reason the reason to getthe name for
 * @param language the language to display the name
 * @returns The translated name if found or the default name as fallback
 */
export const getTranslatedName = (
  reason: AbsenceReason | undefined,
  language: Language | undefined,
): string => {
  return reason && language
    ? reason?.translations.get(language) || reason?.name
    : reason
      ? reason.name
      : 'n.A.';
};
